<template>
  <div
    id="app"
    class="container-fluid accountants-layout new-style__"
    :class="{
      'with-submenu': withSubmenu,
      horizontal: horizontalAccountantsMenu,
      'control-panel': isControlPanel,
    }"
  >
    <portal-target name="aux-modal" />
    <AccountantsMenu :horizontal="horizontalAccountantsMenu" :logo-color="gkLogoColor" />
    <!-- Control panel topbar -->
    <div class="layout-wrap" :class="[routeClass]">
      <AccountantsTopbar
        v-if="isControlPanel"
        @refresh-page="getCustomer()"
        @customer-disconnected="onCustomerDisconnect()"
      />

      <div class="main-row-wrap">
        <AccountantsControlPanelMenu v-if="isControlPanel && controlPanelCustomerCompany" />
        <!-- <AccountantsDashboardMenu v-if="isDashboard" /> -->
        <AccountantsSettingsMenu v-if="isSettings" />
        <AccountantsLedgerMenu v-if="isLedger" />
        <AccountantsCustomersMenu v-if="isClients" />
        <AccountantsCommunicationMenu v-if="isCommunication" />
        <div class="main-row" :class="[routeClass]">
          <LoadingSpinner v-if="loading" />
          <transition v-else name="accountantSectionFadeIn" appear mode="out-in">
            <router-view class="w-100 content-wrap" :class="[routeClass]" />
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'

import { adjustColor, bwFor } from '@/utils/'

import LoadingSpinner from '@/components/dashboard/ui/LoadingSpinner'

import AccountantsMenu from '@/components/accountants/menus/AccountantsMenu'
import AccountantsControlPanelMenu from '@/components/accountants/menus/AccountantsControlPanelMenu'
import AccountantsSettingsMenu from '@/components/accountants/menus/AccountantsSettingsMenu'
import AccountantsDashboardMenu from '@/components/accountants/menus/AccountantsDashboardMenu'
import AccountantsLedgerMenu from '@/components/accountants/menus/AccountantsLedgerMenu'
import AccountantsCustomersMenu from '@/components/accountants/menus/AccountantsCustomersMenu'
import AccountantsCommunicationMenu from '@/components/accountants/menus/AccountantsCommunicationMenu'
import AccountantsTopbar from '@/components/accountants/menus/AccountantsTopbar'

export default {
  name: 'Accountants',
  components: {
    LoadingSpinner,
    AccountantsMenu,
    AccountantsControlPanelMenu,
    AccountantsSettingsMenu,
    AccountantsDashboardMenu,
    AccountantsLedgerMenu,
    AccountantsCustomersMenu,
    AccountantsCommunicationMenu,
    AccountantsTopbar,
  },
  data() {
    return {
      loading: true,
      currentMetaTitle: '',
      horizontalAccountantsMenu: true,
      gkLogoColor: 'white',
    }
  },
  computed: {
    ...mapGetters({
      preferredLanguage: 'main/getPreferredLanguage',
      currentCompany: 'main/getCurrentCompany',
      currentUser: 'main/getCurrentUser',
      visitingCompanyId: 'main/getCompanyId',
      controlPanelCustomer: 'accountants/getControlPanelCustomer',
      controlPanelCustomerProfile: 'accountants/getControlPanelCustomerProfile',
      controlPanelCustomerCompany: 'accountants/getControlPanelCustomerCompany',
    }),
    currentPage() {
      return this.$route.path
    },
    isSettings() {
      const settingsPages = ['companies', 'profile', 'password']
      return settingsPages.some((segment) => this.currentPage.includes(segment))
    },
    isDashboard() {
      const dashboardPage = ['dashboard']
      return dashboardPage.some((segment) => this.currentPage.includes(segment))
    },
    isLedger() {
      return this.currentPage.includes('ledger')
    },
    isClients() {
      const customersPages = ['clients', 'pending-invitations']
      return customersPages.some((segment) => this.currentPage.includes(segment))
    },
    isCommunication() {
      const communicationPages = ['communication']
      return communicationPages.some((segment) => this.currentPage.includes(segment))
    },
    isControlPanel() {
      return this.currentPage.includes('control-panel')
    },
    withSubmenu() {
      return this.isControlPanel || this.isSettings || this.isLedger || this.isClients || this.isCommunication
    },
    brandingColor() {
      if (this.currentCompany.accountant_branding) {
        return this.currentCompany.accountant_branding.color
      } else {
        return '#22AC8A' // $colorBrand
      }
    },
    routeClass() {
      return this.$route && this.$route.name ? this.$route.name.toLowerCase() : ''
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(to, from) {
        this.currentMetaTitle = to.meta.title
        document.title = `${to.meta.title} - Gekko`
        // Call only if customerId changed (or on first load)
        if (!from || to.params.customerId !== from.params.customerId) {
          this.getCustomer()
        }
      },
    },
    brandingColor: {
      immediate: true,
      handler(newBrandingColor) {
        const lightBgColor = adjustColor(newBrandingColor, { lightness: 0.7, gray: 0, perceptual: true })
        const topbarBgColor = adjustColor(newBrandingColor, { lightness: 0.9, gray: 0.3, perceptual: true })
        // const submenuBgColor = adjustColor(newBrandingColor, { lightness: 0.8, gray: 0.5, perceptual: true })
        const submenuBgColor = '#f5f5f5'
        const textHLColor = bwFor(newBrandingColor)

        this.gkLogoColor = textHLColor === '#FFFFFF' ? 'white' : 'brand'

        document.documentElement.style.setProperty('--branding-color', newBrandingColor)
        document.documentElement.style.setProperty('--light-branding-color', lightBgColor)
        document.documentElement.style.setProperty('--submenu-background-color', submenuBgColor)
        document.documentElement.style.setProperty('--text-hl-branding-color', textHLColor)
        document.documentElement.style.setProperty('--topbar-background-color', topbarBgColor)
      },
    },
  },
  mounted () {
    this.attachMixpanelClickEvents()
  },
  created() {
    this.setupData()
  },
  methods: {
    ...mapActions({
      loadSiteConfig: 'main/loadSiteConfig',
      loadMainGlobalData: 'main/loadMainGlobalData',
      loadControlPanelCustomerData: 'accountants/loadControlPanelCustomerData',
      unloadControlPanelCustomerData: 'accountants/unloadControlPanelCustomerData',
    }),
    ...mapMutations({
      setVisitingCompanyId: 'main/SET_VISITING_COMPANY_ID',
      resetVisitingCookie: 'main/SET_VISITING_COOKIE',
    }),
    setupData() {
      this.loadSiteConfig().then((urlsObj) => {
        window.Urls = urlsObj
        this.$root.constructor.prototype.Urls = urlsObj
        this.loadMainGlobalData().then(() => {
          this.$language.current = this.preferredLanguage
          this.loading = false
          // reset visiting company cookie
          this.resetVisitingCookie(null)
        })
      })

      this.$store.commit('setRoot', this.$root)
    },
    openSeeAccount() {
      // TODO this
      window.location.href = '/' + this.preferredLanguage + '/dashboard?vc_id=' + this.$route.params['customerId']
    },
    getCustomer() {
      if (this.$route.params['customerId']) {
        this.loading = true
        this.setVisitingCompanyId(this.$route.params['customerId'])
        this.loadControlPanelCustomerData(this.$route.params['customerId']).then(() => {
          this.loading = false
        })
      } else {
        this.unloadControlPanelCustomerData()
        this.setVisitingCompanyId(null)
        // this.loading = false
      }
    },
    onCustomerDisconnect() {
      this.$router.push({ name: 'Clients' })
    },
    attachMixpanelClickEvents() {
      // TODO refactor mixpanel integration
      setTimeout(() => {
        if (window.mixpanel) {
          var queryElements = document.querySelectorAll('[data-analytics-click]')
          for (let i = 0; i < queryElements.length; i++) {
            const target_name = queryElements[i].getAttribute('data-analytics-click')
            queryElements[i].addEventListener('click', () => {
              const eventData = {
                target: 'Accountants: ' + target_name,
                url: this.$route.path,
                referrer: document.referrer,
                'is logged in': true,
              }
              console.log('clicked on', eventData)
              window.mixpanel.track('click', eventData)
            })
          }
        }
      }, 1000)
    },
  },
}
</script>

<style lang="scss">
* {
  // outline: 1px solid blue;
}
$accMenuSize: 72px;
$accSubmenuSize: 54px;
$topMenuSize: 56px;
:root {
  --accountants-menu-size: #{$accMenuSize};
  --accountants-submenu-size: #{$accSubmenuSize};
  --top-menu-size: #{$topMenuSize};
  --main-row-height: #{$accMenuSize + $accSubmenuSize};
}
.vue-portal-target {
  z-index: 1100;
}
#app.accountants-layout {
  background: var(--branding-color) !important;
  flex-direction: row;
  padding: 0;
  .main-row-wrap {
    display: flex;
    width: 100%;
    height: 100%;
    &.horizontal {
      flex-direction: column;
    }
  }
  &.horizontal {
    flex-direction: column;
    .main-row {
      height: calc(100vh - calc(0.5rem + var(--accountants-menu-size)));
    }
    &.control-panel {
      .main-row {
        height: calc(100vh - calc(0.5rem + var(--main-row-height)));
      }
    }
    .layout-wrap {
      width: 100vw;
      width: calc(100vw - 1rem);
      height: calc(100vh - var(--accountants-menu-size));
      margin-left: 1rem;
      background: white;
      border-top-left-radius: 16px;
      .accountants-topbar {
        border-top-left-radius: 16px;
      }
      .accountants-submenu {
        background: var(--submenu-background-color) !important;
      }

      &.controlpanelinvoices,
      &.controlpanelcosts,
      &.controlpanelfiles {
        height: calc(100vh - var(--accountants-menu-size));
      }
      &.accountantsdashboard {
        .titlebar.dashboard {
          border-top-left-radius: 16px;
        }
      }
    }
    &:not(.control-panel) {
      .layout-wrap {
        .accountants-submenu {
          border-top-left-radius: 16px;
        }
      }
    }
  }
  .side-wrapper {
    display: flex;
    flex-direction: row;
    .accountants-menu {
      background: var(--branding-color) !important;
      overflow: hidden;
      width: var(--accountants-menu-size);
    }
    .navbar-controlpanel {
      background: var(--submenu-branding-color) !important;
    }
    .accountants-menu,
    .navbar-controlpanel {
      padding-top: 1rem;
    }
  }
  &.control-panel {
    .main-row {
      padding-top: 0;
      margin: 0rem;
      border-radius: 0px;
    }
  }

  .edit-design-button {
    position: absolute;
    top: 4rem;
    right: 1.5rem;
    box-shadow: rgba(11, 20, 26, 0.16) 0px 2px 10px 0px, rgba(11, 20, 26, 0.26) 0px 2px 5px 0px !important;
    z-index: 10;
  }
  .layout-wrap {
    display: flex;
    flex-direction: column;
    width: calc(100vw - var(--accountants-menu-size));
    height: calc(100vh - var(--top-menu-size));
    &.controlpanelinvoices,
    &.controlpanelcosts,
    &.controlpanelfiles {
      height: 100vh;
    }
  }

  .main-row {
    position: relative;
    height: 100%;
    overflow: auto;
    width: auto;
    .os-host {
      height: 100%;
    }
    .content-wrap {
      display: flex;
      margin: auto !important;
      max-width: 100% !important;
      height: 100%;
      .tax-report-list-wrap,
      .tax-return-list-wrap {
        display: flex;
        flex-direction: column;
        flex: 1;
      }
      .content {
        flex: 1;
        background: white;
        padding: 1rem;
      }
    }
  }

  .main-row .content-wrap {
    &:not(.controlpanelbanktransaction) {
      .titlebar {
        position: sticky;
        top: 0;
        z-index: 900;
        padding: 0.5rem 1rem;
        border-bottom: 1px solid $colorBorderDash;
        h2 {
          font-size: 24px !important;
        }
        background: white;
        .title-wrap {
          min-height: 50px;
        }
      }
    }
    &.controlpanelbanktransaction {
      .titlebar {
        padding: 0.5rem 1rem;
      }
    }
  }

  .main-row,
  .main-row .content-wrap {
    flex-direction: column;
    flex: 1;
    > .content {
      .content--inner {
        border: 1px solid $colorBorderDash;
        border-radius: $radiusLg;
        overflow: hidden;
        overflow-x: auto;
        box-shadow: $contentShadow;
        height: calc(100% - 44px);
      }
      .cost-table,
      .invoices-table,
      .files-table,
      .customers-table,
      .invitations-table {
        height: 100%;
      }
      .filterbar {
        margin-bottom: 0.5rem;
      }
    }
  }

  .main-row.dashboard {
    background: $colorBgDash;
  }

  .content--inner {
    border: 1px solid $colorBorderDash;
    border-radius: $radiusLg;
    overflow: hidden;
    overflow-x: auto;
    box-shadow: $contentShadow;
  }
}

// updated layout
// #app.accountants-layout.new-style {
//   $layoutGap: 0.5rem;
//   background: linear-gradient(180deg, var(--branding-color) 85%, #ffffff 100%) !important;
//   .accountants-menu {
//     background: transparent;
//   }
//   .layout-wrap {
//     background: transparent;
//     width: 100%;
//     border-radius: $radiusLg;
//     gap: $layoutGap;
//     margin: 0;
//     padding: 1rem;
//     padding-top: 0;
//     .main-row-wrap {
//       gap: $layoutGap;
//       background: transparent;
//       .main-row {
//         border-radius: $radiusLg;
//         background: white;
//         border: 1px solid $colorBorderDash;
//         height: calc(100vh - calc(1rem + var(--accountants-menu-size)));
//       }
//     }
//     .accountants-topbar {
//       background: white;
//       border-radius: $radiusLg;
//       border: 1px solid $colorBorderDash;
//     }
//     .accountants-submenu {
//       padding: 0.5rem;

//       background: #FAFAFA;
//       border-radius: $radiusLg;
//       border: 1px solid $colorBorderDash;

//       ul.accountants-menu--menuitems li.menuitem a:before {
//         border-radius: $radiusMd;
//         // border: 1px solid #e9e9e9;
//       }
//       ul.accountants-menu--menuitems li.menuitem.selected-tab a:before {
//         // border-radius: $radiusMd;
//         border: 1px solid #e9e9e9;
//       }
//     }
//   }
// }
</style>